
import { useMemo, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { ProcurementFormValues } from "../../schema";

interface UseCostCalculationsProps {
  form: UseFormReturn<ProcurementFormValues>;
  onSubtotalChange?: (subtotal: number) => void;
}

export const useCostCalculations = ({ form, onSubtotalChange }: UseCostCalculationsProps) => {
  // Watch items array and individual item properties for deep reactivity
  const items = form.watch("items") || [];
  
  // Watch individual item properties to ensure deep reactivity
  const itemPrices = items.map((_, index) => form.watch(`items.${index}.price_per_unit`));
  const itemQuantities = items.map((_, index) => form.watch(`items.${index}.quantity`));
  
  // Watch other cost fields
  const transportCost = form.watch("transport_cost_per_unit") || 0;
  const loadingCost = form.watch("loading_unloading_cost_per_unit") || 0;
  const agentCost = form.watch("agent_cost_per_unit") || 0;
  const otherCost = form.watch("other_cost_per_unit") || 0;
  const vatApplicable = form.watch("vat_applicable") || false;

  // Calculate totals from items with improved dependency tracking
  const { totalQuantity, totalItemAmount } = useMemo(() => {
    console.log('=== CALCULATING TOTALS ===');
    console.log('Items:', items);
    console.log('Item prices:', itemPrices);
    console.log('Item quantities:', itemQuantities);
    
    if (!items || items.length === 0) {
      console.log('No items found, returning zeros');
      return { totalQuantity: 0, totalItemAmount: 0 };
    }
    
    let quantity = 0;
    let amount = 0;
    
    items.forEach((item, index) => {
      const itemPrice = parseFloat(String(item?.price_per_unit)) || 0;
      const itemQuantity = parseFloat(String(item?.quantity)) || 0;
      const itemTotal = itemPrice * itemQuantity;
      
      console.log(`Item ${index + 1}:`, {
        name: item?.product_name || 'Unnamed',
        price: itemPrice,
        quantity: itemQuantity,
        total: itemTotal
      });
      
      quantity += itemQuantity;
      amount += itemTotal;
    });
    
    console.log('Final totals:', { quantity, amount });
    console.log('=== END CALCULATION ===');
    return { totalQuantity: quantity, totalItemAmount: amount };
  }, [items, itemPrices, itemQuantities]); // Include individual property arrays in dependencies

  // Calculate total unit cost (per unit additional costs)
  const totalUnitCost = useMemo(() => {
    const transport = Number(transportCost) || 0;
    const loading = Number(loadingCost) || 0;
    const agent = Number(agentCost) || 0;
    const other = Number(otherCost) || 0;
    const total = transport + loading + agent + other;
    console.log('Total unit cost calculation:', { transport, loading, agent, other, total });
    return total;
  }, [transportCost, loadingCost, agentCost, otherCost]);

  // Calculate total additional costs
  const totalAdditionalCosts = useMemo(() => {
    const total = totalUnitCost * totalQuantity;
    console.log('Total additional costs:', { totalUnitCost, totalQuantity, total });
    return total;
  }, [totalUnitCost, totalQuantity]);

  // Calculate grand total
  const grandTotal = useMemo(() => {
    const total = totalItemAmount + totalAdditionalCosts;
    console.log('Grand total calculation:', { totalItemAmount, totalAdditionalCosts, total });
    return total;
  }, [totalItemAmount, totalAdditionalCosts]);

  // Update the form's total_amount whenever the grand total changes
  useEffect(() => {
    console.log('Updating form total_amount to:', grandTotal);
    form.setValue("total_amount", grandTotal);
  }, [grandTotal, form]);

  // Notify parent component of subtotal changes
  useEffect(() => {
    console.log('Notifying parent of subtotal change:', totalItemAmount);
    if (onSubtotalChange) {
      onSubtotalChange(totalItemAmount);
    }
  }, [totalItemAmount, onSubtotalChange]);

  return {
    totalQuantity,
    totalItemAmount,
    totalUnitCost,
    totalAdditionalCosts,
    grandTotal,
    vatApplicable,
    otherCost,
    itemsCount: items.length
  };
};
